import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiClient, DiallerImportResult, DiallerList, DiallerRecord,
} from '@arcnet/contracts';
import { api } from './client.js';

/** Dialler lists (docs/36 D1b). The client's own: nobody above the account can read them (0123). */
const dk = (tenantId: string, ...rest: string[]) => ['dialler', tenantId, ...rest] as const;

export const useDiallerLists = (tenantId: string) =>
  useQuery({
    queryKey: dk(tenantId, 'lists'),
    queryFn: () => api.get<{ data: DiallerList[] }>(`/v1/tenants/${tenantId}/dialler/lists`),
    enabled: !!tenantId,
  });

export const useDiallerRecords = (
  tenantId: string, listId: string | null, f: { q?: string; state?: string; offset?: number },
) =>
  useQuery({
    queryKey: dk(tenantId, 'records', listId ?? '', f.q ?? '', f.state ?? '', String(f.offset ?? 0)),
    queryFn: () => {
      const p = new URLSearchParams({ limit: '100', offset: String(f.offset ?? 0) });
      if (f.q) p.set('q', f.q);
      if (f.state) p.set('state', f.state);
      return api.get<{ data: DiallerRecord[]; total: number }>(
        `/v1/tenants/${tenantId}/dialler/lists/${listId}/records?${p}`);
    },
    enabled: !!tenantId && !!listId,
    placeholderData: (prev) => prev,
  });

/** What feeds a list from outside (docs/38): API clients with the scope leads:write. */
export const useDiallerApiClients = (tenantId: string) =>
  useQuery({
    queryKey: dk(tenantId, 'api-clients'),
    queryFn: () => api.get<{ data: ApiClient[] }>(`/v1/tenants/${tenantId}/dialler/api-clients`),
    enabled: !!tenantId,
  });

export const useDiallerDnc = (tenantId: string, q: string) =>
  useQuery({
    queryKey: dk(tenantId, 'dnc', q),
    queryFn: () => api.get<{ data: Array<{ phone: string; reason: string | null; created_at: string }>; total: number }>(
      `/v1/tenants/${tenantId}/dialler/dnc${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled: !!tenantId,
    placeholderData: (prev) => prev,
  });

function useDiallerMutation<V, R = unknown>(tenantId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dialler', tenantId] }); },
  });
}

export const useSaveDiallerList = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
    id ? api.put<{ id: string }>(`/v1/tenants/${tenantId}/dialler/lists/${id}`, body)
      : api.post<{ id: string }>(`/v1/tenants/${tenantId}/dialler/lists`, body));

export const useDeleteDiallerList = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/lists/${id}`));

export const useDeleteDiallerRecord = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/records/${id}`));

/** One slice of a spreadsheet. Not invalidating on every slice: the page refreshes once, at the end. */
export const useImportSlice = (tenantId: string, listId: string) =>
  useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<DiallerImportResult>(`/v1/tenants/${tenantId}/dialler/lists/${listId}/import`, body),
  });

/** The secret comes back this once. */
export const useCreateApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, (body: { name: string; list_id: string }) =>
    api.post<ApiClient & { secret: string }>(`/v1/tenants/${tenantId}/dialler/api-clients`, body));

export const useUpdateApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/v1/tenants/${tenantId}/dialler/api-clients/${id}`, body));

export const useRevokeApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/api-clients/${id}`));

export const useAddDnc = (tenantId: string) =>
  useDiallerMutation(tenantId, (body: { phones: string[]; reason: string | null }) =>
    api.post<{ added: number; already: number; not_numbers: number; suppressed_on_lists: number }>(
      `/v1/tenants/${tenantId}/dialler/dnc`, body));

export const useRemoveDnc = (tenantId: string) =>
  useDiallerMutation(tenantId, (phone: string) => api.delete(`/v1/tenants/${tenantId}/dialler/dnc/${phone}`));
